import React, { useState } from 'react';
import { 
  Building2, Users, FolderKanban, FileText, Plus, Trash2, 
  ChevronRight, ChevronLeft, Download, Check, Sparkles,
  Mail, Briefcase, Calendar
} from 'lucide-react';
import { COGNITIVE_LANES, COGNITIVE_LEVELS, INDUSTRIES } from './onboardingData';

// ================================================
// ORGANIZATION SETUP (Executive Step 4)
// ================================================
export const OrganizationSetup = ({ 
  orgName, setOrgName,
  roleTitle, setRoleTitle,
  industry, setIndustry,
  structureType, setStructureType,
  businessUnits, setBusinessUnits,
  laneColor,
  onNext,
  onBack
}) => {
  const [newUnitName, setNewUnitName] = useState('');
  const [newUnitColor, setNewUnitColor] = useState('#F59E0B');
  
  const unitColors = ['#F59E0B', '#EC4899', '#8B5CF6', '#3B82F6', '#10B981', '#EF4444'];
  
  const addBusinessUnit = () => {
    if (newUnitName.trim()) {
      setBusinessUnits(prev => [...prev, { name: newUnitName.trim(), color: newUnitColor }]);
      setNewUnitName('');
    }
  };
  
  const removeBusinessUnit = (index) => {
    setBusinessUnits(prev => prev.filter((_, i) => i !== index));
  };
  
  const canContinue = orgName.trim() && roleTitle.trim() && industry && structureType;
  
  return (
    <div className="min-h-screen px-6 py-16">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span 
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{ backgroundColor: `${laneColor}15`, color: laneColor }}
          >
            STEP 4 OF 7 — YOUR ORGANIZATION
          </span>
          <h2 className="text-3xl font-bold text-white mb-4">
            Now let's build your organization.
          </h2>
        </div>
        
        {/* Form */}
        <div className="space-y-6">
          {/* Organization Name */}
          <div>
            <label className="block text-sm font-medium text-[#6B8C78] mb-2">
              Organization Name
            </label>
            <input
              type="text"
              value={orgName}
              onChange={(e) => setOrgName(e.target.value)}
              placeholder="Enter your organization name"
              className="w-full px-4 py-3 rounded-xl bg-[#0D1810] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors"
              data-testid="org-name-input"
            />
          </div>
          
          {/* Role/Title */}
          <div>
            <label className="block text-sm font-medium text-[#6B8C78] mb-2">
              Your Role / Title
            </label>
            <input
              type="text"
              value={roleTitle}
              onChange={(e) => setRoleTitle(e.target.value)}
              placeholder="e.g., CEO, Founder, Director"
              className="w-full px-4 py-3 rounded-xl bg-[#0D1810] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors"
              data-testid="role-title-input"
            />
          </div>
          
          {/* Industry */}
          <div>
            <label className="block text-sm font-medium text-[#6B8C78] mb-2">
              Industry
            </label>
            <select
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="w-full px-4 py-3 rounded-xl bg-[#0D1810] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors appearance-none cursor-pointer"
              data-testid="industry-select"
            >
              <option value="" className="bg-[#0D1810]">Select your industry</option>
              {INDUSTRIES.map(ind => (
                <option key={ind} value={ind} className="bg-[#0D1810]">{ind}</option>
              ))}
            </select>
          </div>
          
          {/* Structure Type */}
          <div>
            <label className="block text-sm font-medium text-[#6B8C78] mb-3">
              How is your organization structured?
            </label>
            <div className="grid grid-cols-1 gap-3">
              {[
                { id: 'single_team', label: 'Single team or department', desc: 'Simple flat structure' },
                { id: 'multi_department', label: 'Multiple departments or regions', desc: 'Multi-department structure' },
                { id: 'business_units', label: 'Parent company with distinct business units', desc: 'For orgs with Unique IP entities' }
              ].map(option => (
                <button
                  key={option.id}
                  onClick={() => setStructureType(option.id)}
                  className={`p-4 rounded-xl text-left transition-all ${
                    structureType === option.id ? 'scale-[1.01]' : 'hover:scale-[1.005]'
                  }`}
                  style={{
                    backgroundColor: '#0D1810',
                    border: structureType === option.id ? `2px solid ${laneColor}` : '1px solid #1A2E1F'
                  }}
                  data-testid={`structure-${option.id}`}
                >
                  <div className="flex items-center gap-3">
                    <div 
                      className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                        structureType === option.id ? 'border-transparent' : 'border-[#1A2E1F]'
                      }`}
                      style={structureType === option.id ? { backgroundColor: laneColor } : {}}
                    >
                      {structureType === option.id && (
                        <Check className="w-3 h-3 text-[#080F0D]" />
                      )}
                    </div>
                    <div>
                      <p className="text-white font-medium">{option.label}</p>
                      <p className="text-xs text-[#6B8C78]">{option.desc}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
          
          {/* Business Units (if applicable) */}
          {structureType === 'business_units' && (
            <div className="p-6 rounded-xl" style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}>
              <label className="block text-sm font-medium text-white mb-4">
                Name your business unit(s)
              </label>
              
              {/* Existing Units */}
              {businessUnits.length > 0 && (
                <div className="space-y-2 mb-4">
                  {businessUnits.map((unit, idx) => (
                    <div 
                      key={idx}
                      className="flex items-center justify-between p-3 rounded-lg"
                      style={{ backgroundColor: `${unit.color}15`, border: `1px solid ${unit.color}30` }}
                    >
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: unit.color }}
                        />
                        <span className="text-white">{unit.name}</span>
                      </div>
                      <button
                        onClick={() => removeBusinessUnit(idx)}
                        className="text-[#6B8C78] hover:text-red-400 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
              
              {/* Add New Unit */}
              <div className="flex gap-2">
                <input
                  type="text"
                  value={newUnitName}
                  onChange={(e) => setNewUnitName(e.target.value)}
                  placeholder="e.g., Soul Food, Creative Studio"
                  className="flex-1 px-4 py-2 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && addBusinessUnit()}
                />
                <div className="flex gap-1">
                  {unitColors.map(color => (
                    <button
                      key={color}
                      onClick={() => setNewUnitColor(color)}
                      className={`w-8 h-8 rounded-lg transition-transform ${
                        newUnitColor === color ? 'scale-110 ring-2 ring-white' : ''
                      }`}
                      style={{ backgroundColor: color }}
                    />
                  ))}
                </div>
                <button
                  onClick={addBusinessUnit}
                  className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                  style={{ backgroundColor: laneColor, color: '#080F0D' }}
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
        
        {/* Navigation */}
        <div className="flex justify-between mt-10">
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-xl font-medium text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={onNext}
            disabled={!canContinue}
            className={`px-8 py-3 rounded-xl font-semibold flex items-center gap-2 transition-all ${
              canContinue ? 'hover:scale-105' : 'opacity-50 cursor-not-allowed'
            }`}
            style={canContinue ? { backgroundColor: laneColor, color: '#080F0D' } : { backgroundColor: '#1A2E1F', color: '#6B8C78' }}
            data-testid="org-continue-btn"
          >
            Continue
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================
// TEAM BUILDER (Executive Step 5)
// ================================================
export const TeamBuilder = ({
  teamMembers, setTeamMembers,
  laneColor,
  onNext,
  onBack
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newMember, setNewMember] = useState({
    name: '',
    email: '',
    roleTitle: '',
    primaryLane: '',
    level: '',
    onboardingType: 'self_discover',
    personalNote: ''
  });
  
  const addMember = () => {
    if (newMember.name && newMember.email) {
      setTeamMembers(prev => [...prev, { ...newMember, id: Date.now() }]);
      setNewMember({
        name: '',
        email: '',
        roleTitle: '',
        primaryLane: '',
        level: '',
        onboardingType: 'self_discover',
        personalNote: ''
      });
      setShowAddForm(false);
    }
  };
  
  const removeMember = (id) => {
    setTeamMembers(prev => prev.filter(m => m.id !== id));
  };
  
  return (
    <div className="min-h-screen px-6 py-16">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span 
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{ backgroundColor: `${laneColor}15`, color: laneColor }}
          >
            STEP 5 OF 7 — YOUR TEAM
          </span>
          <h2 className="text-3xl font-bold text-white mb-4">
            Add your team members and assign their cognitive profiles.
          </h2>
          <p className="text-[#6B8C78] max-w-xl mx-auto">
            You can assign profiles yourself based on your knowledge of each person, or let them discover their own through their onboarding.
          </p>
        </div>
        
        {/* Team Members List */}
        {teamMembers.length > 0 && (
          <div className="space-y-3 mb-6">
            {teamMembers.map(member => {
              const lane = member.primaryLane ? COGNITIVE_LANES[member.primaryLane] : null;
              return (
                <div 
                  key={member.id}
                  className="flex items-center justify-between p-4 rounded-xl"
                  style={{ 
                    backgroundColor: '#0D1810', 
                    border: '1px solid #1A2E1F',
                    borderLeft: lane ? `4px solid ${lane.color}` : '1px solid #1A2E1F'
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div 
                      className="w-10 h-10 rounded-full flex items-center justify-center text-white font-bold"
                      style={{ backgroundColor: lane?.color || '#6B8C78' }}
                    >
                      {member.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-white font-medium">{member.name}</p>
                      <p className="text-xs text-[#6B8C78]">{member.roleTitle || member.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {lane && (
                      <span 
                        className="px-2 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${lane.color}20`, color: lane.color }}
                      >
                        {lane.name}
                      </span>
                    )}
                    {member.level && (
                      <span className="text-xs text-[#6B8C78]">{member.level}</span>
                    )}
                    <button
                      onClick={() => removeMember(member.id)}
                      className="text-[#6B8C78] hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Add Member Form */}
        {showAddForm ? (
          <div 
            className="p-6 rounded-xl mb-6"
            style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
          >
            <h4 className="text-white font-semibold mb-4">Add Team Member</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              <input
                type="text"
                value={newMember.name}
                onChange={(e) => setNewMember(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name"
                className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors"
              />
              <input
                type="email"
                value={newMember.email}
                onChange={(e) => setNewMember(prev => ({ ...prev, email: e.target.value }))}
                placeholder="Email"
                className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors"
              />
              <input
                type="text"
                value={newMember.roleTitle}
                onChange={(e) => setNewMember(prev => ({ ...prev, roleTitle: e.target.value }))}
                placeholder="Role / Title"
                className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors"
              />
              <select
                value={newMember.primaryLane}
                onChange={(e) => setNewMember(prev => ({ ...prev, primaryLane: e.target.value }))}
                className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors"
              >
                <option value="">Primary Lane (optional)</option>
                {Object.values(COGNITIVE_LANES).map(lane => (
                  <option key={lane.id} value={lane.id}>{lane.name}</option>
                ))}
              </select>
              <select
                value={newMember.level}
                onChange={(e) => setNewMember(prev => ({ ...prev, level: e.target.value }))}
                className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors"
              >
                <option value="">Level (optional)</option>
                {Object.values(COGNITIVE_LEVELS).map(level => (
                  <option key={level.id} value={level.id}>{level.name}</option>
                ))}
              </select>
              <select
                value={newMember.onboardingType}
                onChange={(e) => setNewMember(prev => ({ ...prev, onboardingType: e.target.value }))}
                className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors"
              >
                <option value="self_discover">Self-discover profile</option>
                <option value="auto_assign">Auto-assign profile</option>
              </select>
            </div>
            <textarea
              value={newMember.personalNote}
              onChange={(e) => setNewMember(prev => ({ ...prev, personalNote: e.target.value }))}
              placeholder="Add a personal note for this team member (optional)"
              rows={2}
              className="w-full px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors resize-none mb-4"
            />
            <div className="flex gap-3">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 rounded-lg text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addMember}
                disabled={!newMember.name || !newMember.email}
                className="px-4 py-2 rounded-lg font-medium transition-colors"
                style={{ 
                  backgroundColor: newMember.name && newMember.email ? laneColor : '#1A2E1F',
                  color: newMember.name && newMember.email ? '#080F0D' : '#6B8C78'
                }}
              >
                Add Member
              </button>
            </div>
          </div>
        ) : (
          <button
            onClick={() => setShowAddForm(true)}
            className="w-full p-4 rounded-xl border-2 border-dashed border-[#1A2E1F] text-[#6B8C78] hover:border-[#00E5A0] hover:text-[#00E5A0] transition-colors flex items-center justify-center gap-2"
            data-testid="add-team-member-btn"
          >
            <Plus className="w-5 h-5" />
            Add Team Member
          </button>
        )}
        
        {/* Skip Link */}
        <p className="text-center text-sm text-[#6B8C78] mt-4">
          <button 
            onClick={onNext}
            className="hover:text-[#00E5A0] transition-colors underline"
          >
            Skip for now — I'll invite my team later
          </button>
        </p>
        
        {/* Navigation */}
        <div className="flex justify-between mt-10">
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-xl font-medium text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={onNext}
            className="px-8 py-3 rounded-xl font-semibold flex items-center gap-2 hover:scale-105 transition-all"
            style={{ backgroundColor: laneColor, color: '#080F0D' }}
            data-testid="team-continue-btn"
          >
            Continue
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================
// PROJECT SEEDER (Executive Step 6)
// ================================================
export const ProjectSeeder = ({
  projects, setProjects,
  teamMembers,
  laneColor,
  onNext,
  onBack
}) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [newProject, setNewProject] = useState({
    name: '',
    description: '',
    suggestedLane: '',
    ownerEmail: '',
    targetDate: ''
  });
  
  const addProject = () => {
    if (newProject.name) {
      setProjects(prev => [...prev, { ...newProject, id: Date.now() }]);
      setNewProject({
        name: '',
        description: '',
        suggestedLane: '',
        ownerEmail: '',
        targetDate: ''
      });
      setShowAddForm(false);
    }
  };
  
  const removeProject = (id) => {
    setProjects(prev => prev.filter(p => p.id !== id));
  };
  
  return (
    <div className="min-h-screen px-6 py-16">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="text-center mb-10">
          <span 
            className="inline-block px-3 py-1 rounded-full text-xs font-semibold mb-4"
            style={{ backgroundColor: `${laneColor}15`, color: laneColor }}
          >
            STEP 6 OF 7 — YOUR FIRST PROJECTS
          </span>
          <h2 className="text-3xl font-bold text-white mb-4">
            Add your active projects so CognitiveOS can start generating intelligence immediately.
          </h2>
          <p className="text-[#6B8C78]">
            You can import from existing tools or add manually.
          </p>
        </div>
        
        {/* Import Options */}
        <div className="grid grid-cols-2 gap-4 mb-8">
          <div 
            className="p-6 rounded-xl text-center opacity-50 cursor-not-allowed"
            style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
          >
            <FolderKanban className="w-8 h-8 text-[#6B8C78] mx-auto mb-3" />
            <p className="text-white font-medium mb-1">Import from existing tools</p>
            <p className="text-xs text-[#6B8C78]">Asana, Monday, ClickUp, Notion, Trello</p>
            <span className="inline-block mt-2 px-2 py-0.5 rounded text-xs bg-[#D4A853]/20 text-[#D4A853]">
              Coming Soon
            </span>
          </div>
          <button
            onClick={() => setShowAddForm(true)}
            className="p-6 rounded-xl text-center hover:scale-[1.02] transition-transform"
            style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
          >
            <Plus className="w-8 h-8 mx-auto mb-3" style={{ color: laneColor }} />
            <p className="text-white font-medium mb-1">Add projects manually</p>
            <p className="text-xs text-[#6B8C78]">Quick and easy project entry</p>
          </button>
        </div>
        
        {/* Projects List */}
        {projects.length > 0 && (
          <div className="space-y-3 mb-6">
            {projects.map(project => {
              const lane = project.suggestedLane ? COGNITIVE_LANES[project.suggestedLane] : null;
              return (
                <div 
                  key={project.id}
                  className="flex items-center justify-between p-4 rounded-xl"
                  style={{ 
                    backgroundColor: '#0D1810', 
                    border: '1px solid #1A2E1F',
                    borderLeft: lane ? `4px solid ${lane.color}` : '1px solid #1A2E1F'
                  }}
                >
                  <div>
                    <p className="text-white font-medium">{project.name}</p>
                    {project.description && (
                      <p className="text-xs text-[#6B8C78] mt-1">{project.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-4">
                    {lane && (
                      <span 
                        className="px-2 py-1 rounded-full text-xs"
                        style={{ backgroundColor: `${lane.color}20`, color: lane.color }}
                      >
                        {lane.name}
                      </span>
                    )}
                    {project.targetDate && (
                      <span className="text-xs text-[#6B8C78]">
                        Due: {new Date(project.targetDate).toLocaleDateString()}
                      </span>
                    )}
                    <button
                      onClick={() => removeProject(project.id)}
                      className="text-[#6B8C78] hover:text-red-400 transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
        
        {/* Add Project Form */}
        {showAddForm && (
          <div 
            className="p-6 rounded-xl mb-6"
            style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
          >
            <h4 className="text-white font-semibold mb-4">Add Project</h4>
            <div className="space-y-4">
              <input
                type="text"
                value={newProject.name}
                onChange={(e) => setNewProject(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Project name"
                className="w-full px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors"
              />
              <textarea
                value={newProject.description}
                onChange={(e) => setNewProject(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Brief description (optional)"
                rows={2}
                className="w-full px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white placeholder-[#4A5E52] focus:outline-none focus:border-[#00E5A0] transition-colors resize-none"
              />
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <select
                  value={newProject.suggestedLane}
                  onChange={(e) => setNewProject(prev => ({ ...prev, suggestedLane: e.target.value }))}
                  className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors"
                >
                  <option value="">Primary Lane</option>
                  {Object.values(COGNITIVE_LANES).map(lane => (
                    <option key={lane.id} value={lane.id}>{lane.name}</option>
                  ))}
                </select>
                <select
                  value={newProject.ownerEmail}
                  onChange={(e) => setNewProject(prev => ({ ...prev, ownerEmail: e.target.value }))}
                  className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors"
                >
                  <option value="">Owner (optional)</option>
                  {teamMembers.map(member => (
                    <option key={member.id} value={member.email}>{member.name}</option>
                  ))}
                </select>
                <input
                  type="date"
                  value={newProject.targetDate}
                  onChange={(e) => setNewProject(prev => ({ ...prev, targetDate: e.target.value }))}
                  className="px-4 py-3 rounded-lg bg-[#080F0D] border border-[#1A2E1F] text-white focus:outline-none focus:border-[#00E5A0] transition-colors"
                />
              </div>
            </div>
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => setShowAddForm(false)}
                className="px-4 py-2 rounded-lg text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={addProject}
                disabled={!newProject.name}
                className="px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2"
                style={{ 
                  backgroundColor: newProject.name ? laneColor : '#1A2E1F',
                  color: newProject.name ? '#080F0D' : '#6B8C78'
                }}
              >
                Add Project
              </button>
              <button
                onClick={() => {
                  addProject();
                  setShowAddForm(true);
                }}
                disabled={!newProject.name}
                className="px-4 py-2 rounded-lg font-medium text-[#6B8C78] hover:text-white transition-colors"
              >
                Add Another
              </button>
            </div>
          </div>
        )}
        
        {/* Skip Link */}
        <p className="text-center text-sm text-[#6B8C78] mt-4">
          <button 
            onClick={onNext}
            className="hover:text-[#00E5A0] transition-colors underline"
          >
            Skip — I'll add projects after setup
          </button>
        </p>
        
        {/* Navigation */}
        <div className="flex justify-between mt-10">
          <button
            onClick={onBack}
            className="px-6 py-3 rounded-xl font-medium text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors flex items-center gap-2"
          >
            <ChevronLeft className="w-4 h-4" />
            Back
          </button>
          <button
            onClick={onNext}
            className="px-8 py-3 rounded-xl font-semibold flex items-center gap-2 hover:scale-105 transition-all"
            style={{ backgroundColor: laneColor, color: '#080F0D' }}
            data-testid="projects-continue-btn"
          >
            Continue
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

// ================================================
// COGNITIVE INTELLIGENCE REPORT (Executive Step 7)
// ================================================
export const CognitiveIntelligenceReport = ({
  primaryLane,
  supportingLanes,
  level,
  cognitiveSignature,
  organization,
  teamMembers,
  laneColor,
  onComplete,
  onBack
}) => {
  const lane = COGNITIVE_LANES[primaryLane];
  const levelData = COGNITIVE_LEVELS[level];
  
  // Generate organizational insights
  const generateInsights = () => {
    if (!teamMembers || teamMembers.length === 0) {
      return {
        laneCoverage: "Add team members to see lane coverage analysis.",
        levelDistribution: "Add team members to see level distribution.",
        cognitiveGaps: "Add team members to identify potential cognitive gaps."
      };
    }
    
    const laneCounts = {};
    const levelCounts = { L1: 0, L2: 0, L3: 0, L4: 0 };
    
    teamMembers.forEach(member => {
      if (member.primaryLane) {
        laneCounts[member.primaryLane] = (laneCounts[member.primaryLane] || 0) + 1;
      }
      if (member.level && levelCounts[member.level] !== undefined) {
        levelCounts[member.level]++;
      }
    });
    
    const coveredLanes = Object.keys(laneCounts);
    const allLanes = Object.keys(COGNITIVE_LANES);
    const missingLanes = allLanes.filter(l => !coveredLanes.includes(l));
    
    return {
      laneCoverage: missingLanes.length === 0 
        ? "Your team has full cognitive lane coverage." 
        : `Your team is missing coverage in ${missingLanes.slice(0, 2).map(l => COGNITIVE_LANES[l]?.name).join(', ')}.`,
      levelDistribution: `Distribution: ${levelCounts.L4} leaders, ${levelCounts.L3} improvers, ${levelCounts.L2} operators, ${levelCounts.L1} executors.`,
      cognitiveGaps: coveredLanes.includes('execution') || coveredLanes.includes('operations')
        ? "Your team has operational coverage."
        : "Consider adding Operations or Execution lane coverage."
    };
  };
  
  const insights = generateInsights();
  
  // Calculate lane distribution percentages
  const totalLanes = 1 + (supportingLanes?.length || 0);
  const primaryPercent = Math.round(100 / totalLanes * 1.5);
  
  return (
    <div className="min-h-screen px-6 py-16">
      <div className="max-w-4xl mx-auto">
        {/* Report Header */}
        <div 
          className="text-center mb-8 p-8 rounded-2xl relative overflow-hidden"
          style={{ 
            background: `linear-gradient(135deg, ${laneColor}20 0%, transparent 50%)`,
            border: `1px solid ${laneColor}30`
          }}
        >
          <span className="text-xs font-semibold tracking-widest text-[#E8D5A3]">
            YOUR COGNITIVE INTELLIGENCE REPORT
          </span>
          <p className="text-sm text-[#6B8C78] mt-1">
            Generated {new Date().toLocaleDateString()} • {organization?.name || 'Your Organization'}
          </p>
        </div>
        
        {/* Section 1: Cognitive Identity */}
        <div 
          className="p-6 rounded-xl mb-6"
          style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
        >
          <h3 className="text-xs font-semibold tracking-widest text-[#6B8C78] mb-4">
            COGNITIVE IDENTITY
          </h3>
          <div className="flex items-start gap-6">
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ backgroundColor: `${lane?.color}20` }}
            >
              <div 
                className="w-6 h-6 rounded-full"
                style={{ backgroundColor: lane?.color, boxShadow: `0 0 20px ${lane?.color}` }}
              />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white mb-1">
                {lane?.name} Thinker
              </h2>
              <p className="text-[#6B8C78] mb-2">Operating at {levelData?.name}</p>
              <p className="text-[#EDF5F0] text-sm leading-relaxed">
                {lane?.description}
              </p>
            </div>
          </div>
        </div>
        
        {/* Section 2: Cognitive Signature Scores */}
        {cognitiveSignature && (
          <div 
            className="p-6 rounded-xl mb-6"
            style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
          >
            <h3 className="text-xs font-semibold tracking-widest text-[#6B8C78] mb-6">
              COGNITIVE SIGNATURE SCORES
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { label: 'Strategic Reach', value: cognitiveSignature.strategicReach, desc: 'Your capacity for long-term thinking' },
                { label: 'Execution Orientation', value: cognitiveSignature.executionOrientation, desc: 'Your drive to complete work' },
                { label: 'Collaborative Capacity', value: cognitiveSignature.collaborativeCapacity, desc: 'Your ability to work across lanes' },
                { label: 'Leadership Bandwidth', value: cognitiveSignature.leadershipBandwidth, desc: 'Your capacity to guide others' }
              ].map(score => (
                <div key={score.label} className="text-center">
                  {/* Circular Gauge */}
                  <div className="relative w-20 h-20 mx-auto mb-3">
                    <svg className="w-full h-full transform -rotate-90">
                      <circle 
                        cx="40" cy="40" r="36" 
                        fill="none" 
                        stroke="#1A2E1F" 
                        strokeWidth="6"
                      />
                      <circle 
                        cx="40" cy="40" r="36" 
                        fill="none" 
                        stroke={lane?.color}
                        strokeWidth="6"
                        strokeLinecap="round"
                        strokeDasharray={`${(score.value / 100) * 226} 226`}
                        style={{ filter: `drop-shadow(0 0 4px ${lane?.color}60)` }}
                      />
                    </svg>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xl font-bold text-white">{score.value}</span>
                    </div>
                  </div>
                  <p className="text-sm font-medium text-white mb-1">{score.label}</p>
                  <p className="text-xs text-[#6B8C78]">{score.desc}</p>
                </div>
              ))}
            </div>
          </div>
        )}
        
        {/* Section 3: Lane Composition */}
        <div 
          className="p-6 rounded-xl mb-6"
          style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
        >
          <h3 className="text-xs font-semibold tracking-widest text-[#6B8C78] mb-4">
            YOUR LANE COMPOSITION
          </h3>
          <div className="flex gap-2 h-8 rounded-lg overflow-hidden mb-4">
            <div 
              className="flex items-center justify-center text-xs font-bold text-[#080F0D]"
              style={{ 
                width: `${primaryPercent}%`,
                backgroundColor: lane?.color 
              }}
            >
              {lane?.name}
            </div>
            {supportingLanes?.map(laneId => {
              const sl = COGNITIVE_LANES[laneId];
              const percent = (100 - primaryPercent) / supportingLanes.length;
              return (
                <div 
                  key={laneId}
                  className="flex items-center justify-center text-xs font-medium text-white"
                  style={{ 
                    width: `${percent}%`,
                    backgroundColor: `${sl?.color}80`
                  }}
                >
                  {sl?.name}
                </div>
              );
            })}
          </div>
          <p className="text-sm text-[#6B8C78]">
            Your cognitive energy is primarily channeled through {lane?.name}, with supporting capacity in 
            {supportingLanes?.length > 0 ? ` ${supportingLanes.map(l => COGNITIVE_LANES[l]?.name).join(' and ')}` : ' your primary lane alone'}.
          </p>
        </div>
        
        {/* Section 4: Organizational Intelligence Preview */}
        {teamMembers && teamMembers.length > 0 && (
          <div 
            className="p-6 rounded-xl mb-6"
            style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
          >
            <h3 className="text-xs font-semibold tracking-widest text-[#6B8C78] mb-4">
              ORGANIZATIONAL INTELLIGENCE PREVIEW
            </h3>
            <div className="space-y-4">
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#080F0D' }}>
                <p className="text-sm text-[#EDF5F0]">
                  <span className="font-semibold" style={{ color: laneColor }}>Lane Coverage: </span>
                  {insights.laneCoverage}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#080F0D' }}>
                <p className="text-sm text-[#EDF5F0]">
                  <span className="font-semibold" style={{ color: laneColor }}>Level Distribution: </span>
                  {insights.levelDistribution}
                </p>
              </div>
              <div className="p-4 rounded-lg" style={{ backgroundColor: '#080F0D' }}>
                <p className="text-sm text-[#EDF5F0]">
                  <span className="font-semibold" style={{ color: laneColor }}>Cognitive Gaps: </span>
                  {insights.cognitiveGaps}
                </p>
              </div>
            </div>
          </div>
        )}
        
        {/* Section 5: Recommended Focus Areas */}
        <div 
          className="p-6 rounded-xl mb-8"
          style={{ backgroundColor: '#0D1810', border: '1px solid #1A2E1F' }}
        >
          <h3 className="text-xs font-semibold tracking-widest text-[#6B8C78] mb-4">
            RECOMMENDED FOCUS AREAS
          </h3>
          <div className="space-y-4">
            <div className="flex gap-3">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${laneColor}20` }}
              >
                <span className="font-bold text-sm" style={{ color: laneColor }}>1</span>
              </div>
              <p className="text-sm text-[#EDF5F0]">
                Start each day in Focus view — it's calibrated to surface what matters most for a {lane?.name} thinker operating at {levelData?.short} level.
              </p>
            </div>
            <div className="flex gap-3">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${laneColor}20` }}
              >
                <span className="font-bold text-sm" style={{ color: laneColor }}>2</span>
              </div>
              <p className="text-sm text-[#EDF5F0]">
                Use the Insights dashboard to monitor cognitive load across your team — as a {lane?.name} leader, you'll see patterns others miss.
              </p>
            </div>
            <div className="flex gap-3">
              <div 
                className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${laneColor}20` }}
              >
                <span className="font-bold text-sm" style={{ color: laneColor }}>3</span>
              </div>
              <p className="text-sm text-[#EDF5F0]">
                Leverage THE LAB for strategic thinking sessions — it's designed to amplify your natural {lane?.name} cognitive strengths.
              </p>
            </div>
          </div>
        </div>
        
        {/* Action Buttons */}
        <div className="flex gap-4 justify-center">
          <button
            onClick={onComplete}
            className="px-8 py-4 rounded-xl font-semibold text-lg flex items-center gap-3 hover:scale-105 transition-transform"
            style={{ 
              backgroundColor: laneColor, 
              color: '#080F0D',
              boxShadow: `0 0 30px ${laneColor}40`
            }}
            data-testid="enter-cognitiveos-btn"
          >
            <Sparkles className="w-5 h-5" />
            Enter CognitiveOS
          </button>
          <button
            onClick={() => {/* PDF download placeholder */}}
            className="px-6 py-4 rounded-xl font-medium text-[#6B8C78] border border-[#1A2E1F] hover:border-[#6B8C78] transition-colors flex items-center gap-2"
          >
            <Download className="w-4 h-4" />
            Download Report
          </button>
        </div>
      </div>
    </div>
  );
};
